import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components';
import { userService } from '../services';
import { useUser, useAuth } from '../context';

/**
 * Mode Select Page (Screen 03) - The Router
 * First-time user chooses between GUEST and HOST mode
 * Design: Inside theme - light background, operational/dense design
 */
export default function ModeSelectPage() {
  const navigate = useNavigate();
  const { profile, fetchProfile, walletBalance } = useUser();
  const { logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redirect if user already has a mode set
  useEffect(() => {
    if (profile?.initialMode !== null && profile?.initialMode !== undefined) {
      // User already selected a mode, redirect to appropriate dashboard
      if (profile.mode === 'host') {
        navigate('/host');
      } else {
        navigate('/guest');
      }
    }
  }, [profile, navigate]);

  const handleModeSelect = async (mode) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await userService.setMode(mode);

      if (result.success) {
        // Refresh profile to get updated mode
        await fetchProfile();

        // Redirect based on selected mode
        if (mode === 'host') {
          navigate('/initialize-credential');
        } else {
          navigate('/initialize-credential');
        }
      } else {
        setError(result.error || 'Failed to set mode. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar
        user={profile}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate('/')}
      />

      <main className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-mono text-3xl md:text-4xl font-bold uppercase tracking-wider text-black mb-4">
            WHAT BRINGS YOU HERE?
          </h1>
          <p className="font-mono text-sm text-gray-600 uppercase tracking-wider">
            SELECT YOUR PRIMARY PATH
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-8 p-4 border-2 border-red-600 bg-red-50">
            <p className="font-mono text-sm text-red-600 text-center uppercase tracking-wider">
              {error}
            </p>
          </div>
        )}

        {/* Mode selection boxes */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Guest Mode Box */}
          <button
            type="button"
            onClick={() => handleModeSelect('guest')}
            disabled={isLoading}
            className={`
              p-8 md:p-12
              border-2 border-black bg-white
              hover:bg-black hover:text-white
              transition-colors duration-200
              text-left
              disabled:opacity-50 disabled:cursor-not-allowed
              group
            `}
          >
            <div className="mb-6">
              <div className="w-16 h-16 border-2 border-current flex items-center justify-center mb-4 group-hover:bg-white group-hover:text-black transition-colors">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h2 className="font-mono text-2xl font-bold uppercase tracking-wider mb-2">
                GUEST
              </h2>
            </div>
            <p className="font-mono text-sm leading-relaxed opacity-80">
              Discover exclusive gatherings. Build your Party Resume by attending events and connecting with like-minded people.
            </p>
            <div className="mt-6 pt-6 border-t border-current opacity-60">
              <ul className="font-mono text-xs space-y-3 tracking-wider">
                <li><span className="uppercase font-bold">Browse Gatherings</span> — Find the rooms and energies that actually align with you.</li>
                <li><span className="uppercase font-bold">Submit Application</span> — Commit your P$ to the party fund while sending your resume to the host.</li>
                <li><span className="uppercase font-bold">Get Selected</span> — Receive your Party Pass and location coordinates once chosen, or get your P$ fully returned.</li>
              </ul>
            </div>
          </button>

          {/* Host Mode Box */}
          <button
            type="button"
            onClick={() => handleModeSelect('host')}
            disabled={isLoading}
            className={`
              p-8 md:p-12
              border-2 border-black bg-white
              hover:bg-black hover:text-white
              transition-colors duration-200
              text-left
              disabled:opacity-50 disabled:cursor-not-allowed
              group
            `}
          >
            <div className="mb-6">
              <div className="w-16 h-16 border-2 border-current flex items-center justify-center mb-4 group-hover:bg-white group-hover:text-black transition-colors">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h2 className="font-mono text-2xl font-bold uppercase tracking-wider mb-2">
                HOST
              </h2>
            </div>
            <p className="font-mono text-sm leading-relaxed opacity-80">
              Create and curate gatherings. Review Party Resumes to find the right guests and build memorable experiences.
            </p>
            <div className="mt-6 pt-6 border-t border-current opacity-60">
              <ul className="font-mono text-xs space-y-2 uppercase tracking-wider">
                <li>- Create gatherings</li>
                <li>- Review Party Resumes</li>
                <li>- Curate your crowd</li>
              </ul>
            </div>
          </button>
        </div>

        {/* Note */}
        <p className="font-mono text-xs text-gray-500 text-center uppercase tracking-wider">
          MODE CAN BE SWITCHED LATER IN SETTINGS
        </p>
      </main>
    </div>
  );
}
